/**
 * Export button component for triggering BTX exports
 */

import { CheckCircle, Download, ErrorOutline } from "@mui/icons-material";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { JSX } from "react";

interface ExportButtonProps {
  onClick: () => void;
  isExporting: boolean;
  hasExportError: boolean;
  hasExportSuccess: boolean;
}

const ease = "all 0.3s ease-in-out";

/**
 * Button for triggering Excel export
 * Shows different states: idle (download icon), exporting (spinner), error (error icon), success (checkmark)
 */
export const ExportButton = ({
  onClick,
  isExporting,
  hasExportError,
  hasExportSuccess,
}: ExportButtonProps): JSX.Element => {
  // Determine which icon to show
  const getExportIcon = (): JSX.Element => {
    if (isExporting) {
      return <CircularProgress size={20} color="brandSupporting" />;
    }
    if (hasExportSuccess) {
      return <CheckCircle color="success" sx={{ transition: ease }} />;
    }
    if (hasExportError) {
      return <ErrorOutline color="error" sx={{ transition: ease }} />;
    }
    return <Download sx={{ transition: ease }} />;
  };

  const getButtonColor = (): "error" | "success" | "default" => {
    if (hasExportError) return "error";
    if (hasExportSuccess) return "success";
    return "default";
  };

  return (
    <Tooltip title="Export to Excel">
      {/* span wrapper so the tooltip still shows while the button is disabled mid-export */}
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          color={getButtonColor()}
          disabled={isExporting}
          aria-label="Export to Excel"
          sx={{
            transition: "color 0.3s ease-in-out",
          }}
        >
          {getExportIcon()}
        </IconButton>
      </span>
    </Tooltip>
  );
};
